import { filter } from 'lodash';
import React from 'react';

import {
  buildOptionsFromData,
  ErrorHandler,
  ExternalLink,
  FilteredTable,
  MultiSelectTableFilter,
  RenderTableColumn,
  Text,
  TextFilterSearch,
} from '@common/components';
import {
  DisputeListItemFragmentFragment,
  DisputeStateType,
  MissionControlPaginatedDisputesQueryVariables,
  PaginatedDisputesFilterInput,
  SupplierNamesQueryCgResponse,
} from '@common/graphql';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { SUPPLIER_NAMES_QUERY } from '@institutions/graphql';
import {
  DisputeActionsDropdown,
  DisputeReviewLink,
  DisputeState,
  DisputeTypeDisplay,
} from '@pay-bill/components';
import { MISSION_CONTROL_PAGINATED_DISPUTES_QUERY } from '@pay-bill/graphql';
import {
  CLEANUP_WHEN_EMPTY,
  getRedirectTargetEntity,
  nonFalse,
} from '@pay-bill/services';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';

type Props = {
  defaultStateFilters: DisputeStateType[];
};

export function MissionControlBillingTable({ defaultStateFilters }: Props) {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;
  const isSupplier = entityType === 'Supplier';

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: isSupplier ? '' : 'MissionControlDisputeTable',
    defaultPaginationData: {
      defaultPageSize: 50,
      limit: 50,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'DATE',
    },
    columnsDictionary: {
      employeeName: 'EMPLOYEE_NAME',
      code: 'CODE',
      disputeType: 'DISPUTE_TYPE',
      state: 'STATE',
      supplierName: 'SUPPLIER',
      date: 'DATE',
      employeeId: 'EMPLOYEE_ID',
      differential: 'DIFFERENTIAL',
      lastActionAt: 'LAST_ACTION_AT',
      neededActions: 'NEEDED_ACTIONS',
      invoiceCode: 'INV_CODE',
      revInvoiceCode: 'RINV_CODE',
      costCenter: 'COST_CENTER',
    },
  });

  const [fetchSupplier, { error: supplierError, data: supplierData }] =
    useLazyQuery(SUPPLIER_NAMES_QUERY);

  const { data, error, loading } = useQuery(
    MISSION_CONTROL_PAGINATED_DISPUTES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        ...paginationVariables,
        orderDirection:
          paginationVariables.orderDirection as MissionControlPaginatedDisputesQueryVariables['orderDirection'],
        orderField:
          paginationVariables.orderField as MissionControlPaginatedDisputesQueryVariables['orderField'],
        filters: {
          ...paginationVariables.filters,
          state: defaultStateFilters,
        },
      },
      onCompleted() {
        fetchSupplier();
      },
    }
  );

  const columns = getDisputeColumns({
    filters: filters as PaginatedDisputesFilterInput,
    isSupplier,
    supplierData,
  });

  const page = data?.missionControlPaginatedDisputes;
  const disputes = page?.disputes || [];
  const totalDisputes = page?.totalCount ?? 0;

  const filtersDataReady = !!supplierData;

  const anyError = error || supplierError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="BillingDisputeTable" />;
  }

  return (
    <div>
      <FilteredTable
        rowKey="id"
        className="mission-control-disputes-table"
        columns={columns}
        dataSource={disputes}
        pagination={{ ...paginationVariables, total: totalDisputes }}
        loading={loading}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={filters}
        localStorageFilterKey={localStorageFilterKey}
        filtersDataReady={filtersDataReady}
      />
      {!loading && (
        <div>
          Showing {disputes.length}.{' '}
          <ExternalLink url="/works/billing/invoices">View All</ExternalLink>.
        </div>
      )}
    </div>
  );
}

const getDisputeColumns = ({
  filters,
  isSupplier,
  supplierData,
}: {
  filters: PaginatedDisputesFilterInput;
  isSupplier: boolean;
  supplierData?: SupplierNamesQueryCgResponse;
}) => {
  return filter<
    RenderTableColumn<DisputeListItemFragmentFragment> | false,
    RenderTableColumn<DisputeListItemFragmentFragment>
  >(
    [
      {
        title: 'Dispute Code',
        key: 'code',
        className: 'dispute-code-col',
        sorter: true,
        filtered: !!filters.code?.length,
        filteredValue: filters.code || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-dispute-code-input"
              placeholder="Filter by code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, dispute: DisputeListItemFragmentFragment) => (
          <DisputeReviewLink disputeCode={dispute.code} />
        ),
      },
      {
        title: 'Type',
        key: 'disputeType',
        dataIndex: 'disputeType',
        className: 'type-col',
        sorter: true,
        render: (type) => type && <DisputeTypeDisplay type={type} />,
      },
      isSupplier && {
        title: 'Reverse Invoice #',
        key: 'revInvoiceCode',
        dataIndex: 'worksReverseInvoice',
        className: 'rev-invoice-code-col',
        width: 200,
        filtered: !!filters.revInvoiceCode?.length,
        filteredValue: filters.revInvoiceCode || CLEANUP_WHEN_EMPTY,
        sorter: true,
        filterFormat: 'id',
        render: (item) => <div>{item?.code}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-rev-invoice-code-input"
              placeholder="Filter by reverse invoice code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      !isSupplier && {
        title: 'Invoice #',
        key: 'invoiceCode',
        dataIndex: 'worksInvoice',
        className: 'invoice-code-col',
        width: 200,
        filtered: !!filters.invoiceCode?.length,
        filteredValue: filters.invoiceCode || CLEANUP_WHEN_EMPTY,
        sorter: true,
        filterFormat: 'id',
        render: (item) => <div>{item?.code}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-invoice-code-input"
              placeholder="Filter by invoice code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Date Worked',
        key: 'date',
        dataIndex: 'newDate',
        className: 'date-col',
        sorter: true,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
      },
      {
        title: 'EEID',
        key: 'employeeId',
        dataIndex: 'employeeId',
        className: 'employee-id-col',
        filterFormat: 'id',
        render: (employeeId) =>
          employeeId ? (
            <div>{employeeId}</div>
          ) : (
            <i style={{ color: Colors.steelDarker }}>Blank</i>
          ),
        sorter: true,
        filtered: !!filters.employeeId?.length,
        filteredValue: filters.employeeId || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-employee-id-input"
              placeholder="Filter by EEID"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Full Name',
        key: 'employeeName',
        className: 'employee-name-col sentry-mask',
        sorter: true,
        filtered: !!filters.employeeName?.length,
        filteredValue: filters.employeeName || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-employee-name-input"
              placeholder="Filter by name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, dispute: DisputeListItemFragmentFragment) => (
          <div>{dispute.staffingRequestAssignment.worksProfile.fullName}</div>
        ),
      },
      !isSupplier && {
        title: 'CC',
        key: 'costCenter',
        dataIndex: 'organizationUnit',
        sorter: true,
        filtered: !!filters.costCenter?.length,
        filteredValue: filters.costCenter || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              onSearch={(value) => {
                value ? setSelectedKeys([value]) : setSelectedKeys([]);
                confirm();
              }}
            />
          </section>
        ),
        render: (organizationUnit) => <div>{organizationUnit.costCenter}</div>,
      },
      !isSupplier && {
        title: 'Supplier',
        key: 'supplierName',
        dataIndex: 'supplier',
        className: 'supplier-name-col',
        filtered: !!filters.supplierName?.length,
        filteredValue: filters.supplierName || CLEANUP_WHEN_EMPTY,
        filterDropdown: MultiSelectTableFilter,
        filters: supplierData && buildOptionsFromData(supplierData.suppliers),
        render: (supplier) => <div>{supplier.name}</div>,
        sorter: true,
      },
      {
        title: 'Status',
        key: 'state',
        width: '200px',
        dataIndex: 'state',
        className: 'state-col',
        sorter: true,
        render: (text, dispute) => (
          <div>
            <DisputeState
              state={text}
              createdBy={getRedirectTargetEntity(
                dispute.createdBy?.entityType as
                  | 'Supplier'
                  | 'Organization'
                  | undefined
              )}
            />
            {dispute.exported && (
              <Text color="midnightLighter" size="sm">
                Exported
              </Text>
            )}
          </div>
        ),
      },
      {
        title: 'Last Updated',
        key: 'lastActionAt',
        dataIndex: 'lastActionAt',
        className: 'last-updated-col',
        sorter: true,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
      },
      {
        title: 'Action',
        render: (_: unknown, dispute: DisputeListItemFragmentFragment) => (
          <DisputeActionsDropdown listType="disputes" dispute={dispute} />
        ),
      },
    ],
    nonFalse
  );
};
